import React from 'react';
import { COLOR_SCHEME } from '../../../styling/ColorSchemes';
import { useScreenSize } from '../../../contexts/ScreenSizeContext';

export type PeriodKey = 'Morning' | 'Afternoon' | 'Evening';

interface TimePickerProps {
  selectedDate: Date | null;
  selectedPeriod: PeriodKey | null;
  setSelectedPeriod: (p: PeriodKey | null) => void;
  selectedTime: string | null;
  onSelectTime: (t: string) => void;
  colorScheme: COLOR_SCHEME;
  periodTimes: Record<PeriodKey, string[]>;
  layoutWidth?: number;
  compactSelectedDayActions?: {
    onChangeDay: () => void;
    onNextDay: () => void;
    nextDayEnabled: boolean;
  };
}

export const TimePicker: React.FC<TimePickerProps> = ({
  selectedDate,
  selectedPeriod,
  setSelectedPeriod,
  selectedTime,
  onSelectTime,
  colorScheme,
  periodTimes,
  layoutWidth,
  compactSelectedDayActions,
}) => {
  const baseScreen = useScreenSize();
  const responsiveWidth = layoutWidth ?? baseScreen.width;
  const screen = {
    width: responsiveWidth,
    isMobile: responsiveWidth < 768,
    isTablet: responsiveWidth >= 768 && responsiveWidth < 1024,
    isDesktop: responsiveWidth >= 1024,
  };
  const allTimes = [...periodTimes.Morning, ...periodTimes.Afternoon, ...periodTimes.Evening];
  const displayedTimes = selectedPeriod ? periodTimes[selectedPeriod] : allTimes;
  const layoutScale = Math.min(1, Math.max(0, (responsiveWidth - 280) / 744));
  const scaledRem = (minimum: number, maximum: number) =>
    `${minimum + (maximum - minimum) * layoutScale}rem`;
  const panelPadding = `${scaledRem(0.65, 1)} ${scaledRem(0.6, 1)} ${scaledRem(0.6, 0.95)}`;
  const controlRadius = scaledRem(0.6, 0.8);
  const filterPadding = `${scaledRem(0.36, 0.5)} ${scaledRem(0.5, 0.9)}`;
  const timeSlotMinWidth = Math.round(82 + (112 - 82) * layoutScale);

  const renderTimeButton = (time: string) => (
    <button
      key={`time-${time}`}
      type="button"
      onClick={() => onSelectTime(time)}
      style={{
        background: selectedTime === time ? colorScheme.PRIMARY : '#FFFFFF',
        border: `1px solid ${selectedTime === time ? colorScheme.PRIMARY : '#D8DCE8'}`,
        borderRadius: scaledRem(0.6, 0.75),
        padding: `${scaledRem(0.5, 0.72)} ${scaledRem(0.4, 0.65)}`,
        minHeight: scaledRem(2.25, 2.75),
        fontSize: scaledRem(0.64, 0.78),
        fontWeight: 700,
        color: selectedTime === time ? '#FFFFFF' : colorScheme.PRIMARY,
        cursor: 'pointer',
        boxShadow: selectedTime === time ? `0 0.55rem 1rem ${colorScheme.PRIMARY}28` : '0 0.2rem 0.55rem rgba(15, 23, 42, 0.06)',
      }}
    >
      {time}
    </button>
  );

  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #D9D9D9',
        borderRadius: scaledRem(0.75, 1),
        padding: panelPadding,
        boxShadow: '0 0.45rem 1rem rgba(15, 23, 42, 0.06)',
      }}
    >
      <div style={{ fontSize: scaledRem(0.6, 0.75), fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>
        Selected day
      </div>
      <div style={{ marginTop: '0.35rem', minHeight: '1.4rem', display: 'flex', flexDirection: 'column', gap: compactSelectedDayActions ? '0.45rem' : '0.15rem' }}>
        {selectedDate ? (
          <>
            <div style={{ fontSize: scaledRem(0.84, 1), fontWeight: 800, color: '#111827' }}>
              {selectedDate.toLocaleString('default', { weekday: 'long', month: 'short', day: 'numeric' })}
            </div>
            {compactSelectedDayActions ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: scaledRem(0.25, 0.38) }}>
                <button
                  type="button"
                  onClick={compactSelectedDayActions.onChangeDay}
                  style={{
                    background: `${colorScheme.PRIMARY}12`,
                    border: `1px solid ${colorScheme.PRIMARY}55`,
                    color: colorScheme.PRIMARY,
                    fontSize: scaledRem(0.58, 0.64),
                    fontWeight: 800,
                    cursor: 'pointer',
                    padding: `${scaledRem(0.38, 0.48)} ${scaledRem(0.4, 0.56)}`,
                    borderRadius: controlRadius,
                    textAlign: 'center',
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    boxShadow: `0 0.3rem 0.8rem ${colorScheme.PRIMARY}18`,
                  }}
                >
                  Change day
                </button>
                <button
                  type="button"
                  onClick={compactSelectedDayActions.onNextDay}
                  disabled={!compactSelectedDayActions.nextDayEnabled}
                  style={{
                    background: compactSelectedDayActions.nextDayEnabled ? colorScheme.PRIMARY : '#F3F4F6',
                    color: compactSelectedDayActions.nextDayEnabled ? '#FFFFFF' : '#9CA3AF',
                    border: `1px solid ${compactSelectedDayActions.nextDayEnabled ? colorScheme.PRIMARY : '#E5E7EB'}`,
                    borderRadius: controlRadius,
                    padding: `${scaledRem(0.38, 0.48)} ${scaledRem(0.4, 0.56)}`,
                    fontSize: scaledRem(0.58, 0.64),
                    fontWeight: 800,
                    cursor: compactSelectedDayActions.nextDayEnabled ? 'pointer' : 'not-allowed',
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    boxShadow: compactSelectedDayActions.nextDayEnabled ? `0 0.45rem 1rem ${colorScheme.PRIMARY}24` : 'none',
                  }}
                >
                  {compactSelectedDayActions.nextDayEnabled ? 'Next day' : 'No next day'}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: '0.73rem', color: '#6B7280' }}>
                Choose any time below to continue.
              </div>
            )}
          </>
        ) : (
          <div style={{ fontSize: scaledRem(0.7, 0.84), fontWeight: 600, color: '#6B7280' }}>
            Pick a day to reveal available appointment times.
          </div>
        )}
      </div>

      {selectedDate && allTimes.length > 0 ? (
        <>
          <div style={{ marginTop: scaledRem(0.65, 0.9), display: 'flex', gap: scaledRem(0.3, 0.5), flexWrap: 'wrap', overflowX: 'visible', paddingBottom: scaledRem(0.15, 0.25), justifyContent: screen.isMobile ? 'center' : 'flex-start' }}>
            {[null, 'Morning', 'Afternoon', 'Evening'].map((period) => {
              const isActive = selectedPeriod === period;
              const label = period ?? 'All times';
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => setSelectedPeriod(period as PeriodKey | null)}
                  style={{
                    background: isActive ? colorScheme.PRIMARY : colorScheme.SURFACE || '#F5F4FB',
                    color: isActive ? '#FFFFFF' : colorScheme.PRIMARY,
                    border: `1px solid ${isActive ? colorScheme.PRIMARY : `${colorScheme.PRIMARY}45`}`,
                    padding: filterPadding,
                    borderRadius: '999px',
                    fontSize: scaledRem(0.6, 0.72),
                    fontWeight: 700,
                    cursor: 'pointer',
                    flex: screen.isMobile ? '0 0 auto' : undefined,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div style={{ marginTop: scaledRem(0.65, 0.9), display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${timeSlotMinWidth}px, 1fr))`, gap: scaledRem(0.35, 0.55) }}>
            {displayedTimes.map((time) => renderTimeButton(time))}
          </div>
        </>
      ) : null}

      {selectedDate && allTimes.length === 0 ? (
        <div
          style={{
            marginTop: '0.9rem',
            borderRadius: '0.85rem',
            border: '1px dashed #D1D5DB',
            background: '#F8FAFC',
            padding: '0.95rem',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#111827' }}>
            No times are available for this day.
          </div>
          <div style={{ marginTop: '0.25rem', fontSize: '0.72rem', color: '#6B7280', lineHeight: 1.5 }}>
            Try another date to see the next open appointment options.
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TimePicker;
